import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Omgång 3: paketvalet på produktsidan.
 * Kunden väljer 1 eller 2 personer/hundar och storlek per person. Valet bockar i Horizons dolda
 * variant-picker och skickar ett change-event, så Horizon hämtar rätt variant (pris, köpknapp, adress).
 * Två personer = värdet «M + L» (ordning som i listan med enstaka storlekar).
 */
class VindelyPaketval extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[data-paketval]');
    this.options = data ? JSON.parse(data.textContent).options : [];
    this.addEventListener('change', this.#onChange);
    this.#syncSpara();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  get #two() {
    const checked = this.querySelector('.vindely-paketval__antal input:checked');
    return checked ? checked.value === '1' : false;
  }

  /** @param {number} optionIndex @param {number} person */
  #code(optionIndex, person) {
    const input = this.querySelector(`[data-person="${person}"] [data-option="${optionIndex}"] input:checked`);
    return input ? input.value : '';
  }

  /** @param {number} optionIndex */
  #valueFor(optionIndex) {
    const values = this.options[optionIndex]?.values || [];
    const singles = values.filter((value) => !value.name.includes(' + '));
    const codeOf = (value) => value.name.split(' ')[0];
    const first = this.#code(optionIndex, 1);
    if (!this.#two) return singles.find((value) => codeOf(value) === first);
    const second = this.#code(optionIndex, 2);
    const order = singles.map(codeOf);
    const pair = [first, second].sort((a, b) => order.indexOf(a) - order.indexOf(b)).join(' + ');
    return values.find((value) => value.name === pair);
  }

  #syncView() {
    const two = this.#two;
    this.dataset.two = String(two);
    const second = this.querySelector('[data-person="2"]');
    if (second) second.hidden = !two;
    for (const label of this.querySelectorAll('.vindely-paketval__vem')) label.hidden = !two;
    // Personen 2 får samma storlek som person 1 första gången, så att kunden bara ändrar om det behövs.
    if (two && second && !second.querySelector('input:checked')) {
      for (const input of this.querySelectorAll('[data-person="1"] input:checked')) {
        const fieldset = input.closest('[data-option]');
        const twin = second.querySelector(`[data-option="${fieldset?.dataset.option}"] input[value="${CSS.escape(input.value)}"]`);
        if (twin instanceof HTMLInputElement) twin.checked = true;
      }
    }
    this.#syncSpara();
  }

  #syncSpara() {
    const two = this.#two;
    const root = this.closest('product-information, .product-information, main') || document;
    for (const text of root.querySelectorAll('[data-spara-antal]')) {
      text.hidden = text.getAttribute('data-spara-antal') !== (two ? '2' : '1');
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.stopPropagation();
    this.#syncView();
    const picker = this.#picker();
    if (!picker) return;
    const ids = [this.options[0]?.values[this.#two ? 1 : 0]?.id];
    for (let index = 1; index < this.options.length; index++) ids.push(this.#valueFor(index)?.id);
    if (ids.some((id) => id === undefined)) return;
    let last = null;
    for (const id of ids) {
      const radio = picker.querySelector(`input[data-option-value-id="${id}"]`);
      if (!(radio instanceof HTMLInputElement)) continue;
      if (!radio.checked) last = radio;
      radio.checked = true;
    }
    if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #picker() {
    let node = this.previousElementSibling;
    while (node && node.tagName !== 'VARIANT-PICKER') node = node.previousElementSibling;
    return node || this.parentElement?.querySelector('variant-picker');
  }
}

if (!customElements.get('vindely-paketval')) customElements.define('vindely-paketval', VindelyPaketval);

/**
 * Omgång 3: «Lägg till»-erbjudandet i korgen. Ett klick lägger varan i korgen; Shopifys automatiska
 * rabatt «Vindely» sänker priset. Horizon morfar korgen från sektionerna i svaret, som Köpskyddet.
 */
class VindelyErbjudande extends HTMLElement {
  static #pending = false;

  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', this.#add);
    this.#visaPris();
  }

  /** Annan valuta än kronor: avdraget (öre) räknas om med Shopifys kurs, som kassan gör. */
  #visaPris() {
    const pris = this.querySelector('[data-erbjudande-pris]');
    const kurs = Number(window.Shopify?.currency?.rate || 0);
    if (!(pris instanceof HTMLElement) || !kurs) return;
    const mall = pris.dataset.mall || '';
    const delar = mall.match(/1(\D?)234(\D)56/);
    if (!delar) return;
    const cent = Number(pris.dataset.pris) - Math.round(Number(pris.dataset.avdrag) * kurs);
    if (!(cent > 0)) return;
    const hel = String(Math.floor(cent / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, delar[1]);
    pris.textContent = mall.replace(delar[0], `${hel}${delar[2]}${String(cent % 100).padStart(2, '0')}`);
    pris.hidden = false;
  }

  disconnectedCallback() {
    this.querySelector('button')?.removeEventListener('click', this.#add);
  }

  #add = async () => {
    if (VindelyErbjudande.#pending) return;
    VindelyErbjudande.#pending = true;
    const button = this.querySelector('button');
    const error = this.querySelector('[data-erbjudande-fel]');
    if (error instanceof HTMLElement) error.hidden = true;
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;

    const variantId = String(this.dataset.variantId);
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sections.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add failed');
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, sections: result.sections, source: 'vindely-erbjudande', didError: false },
      });
    } catch (problem) {
      if (error instanceof HTMLElement) error.hidden = false;
      this.dispatchEvent(new CartErrorEvent({ error: problem?.message || 'Add failed', code: 'INVALID' }));
      try {
        const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: cart.item_count, source: 'vindely-erbjudande', didError: true },
        });
      } catch (readError) {
        deferred.reject(readError);
      }
    } finally {
      VindelyErbjudande.#pending = false;
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  };
}

if (!customElements.get('vindely-erbjudande')) customElements.define('vindely-erbjudande', VindelyErbjudande);
